import {
  FINANCE_OFFICE_COMMAND,
  FINANCE_OFFICE_TOOL,
  validateFinanceOfficeToolName,
} from './contracts';
import { FinanceDocumentPrivacy } from './FinanceDocumentPrivacy';
import { FinanceOfficeOperationRegistry } from './FinanceOfficeOperationRegistry';
import { PowerPointFinanceWriter } from './writers/PowerPointFinanceWriter';
import { ExcelFinanceWriter } from './writers/ExcelFinanceWriter';
import { WordFinanceWriter } from './writers/WordFinanceWriter';

export class FinanceOfficeControllerError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'FinanceOfficeControllerError';
    this.code = code;
  }
}

function controllerError(code, message) {
  return new FinanceOfficeControllerError(code, message);
}

function defaultWriter(app) {
  if (app === 'powerpoint') {
    return [
      FINANCE_OFFICE_COMMAND.POWERPOINT_INSERT_TEXT,
      new PowerPointFinanceWriter(),
    ];
  }
  if (app === 'excel') {
    return [
      FINANCE_OFFICE_COMMAND.EXCEL_WRITE_TABLE,
      new ExcelFinanceWriter(),
    ];
  }
  if (app === 'word') {
    return [
      FINANCE_OFFICE_COMMAND.WORD_INSERT_TEXT,
      new WordFinanceWriter(),
    ];
  }
  throw controllerError(
    'finance_office_unavailable',
    'Finance Office controller requires a supported Office host'
  );
}

function safeReceipt(receipt) {
  return Object.freeze({
    schemaVersion: receipt.schemaVersion,
    operationId: receipt.operationId,
    operationBinding: receipt.operationBinding,
    stepId: receipt.stepId,
    documentId: receipt.documentId,
    app: receipt.app,
    command: receipt.command,
    targetType: receipt.targetType,
    targetId: receipt.targetId,
    status: receipt.status,
    mutationCount: receipt.mutationCount,
    startedAt: receipt.startedAt,
    completedAt: receipt.completedAt,
  });
}

export class FinanceOfficeController {
  constructor({
    app,
    documentPrivacy = null,
    settings = null,
    writer = null,
    now,
  }) {
    const [command, defaultAppWriter] = defaultWriter(app);
    const selectedWriter = writer || defaultAppWriter;
    this.app = app;
    this.liveDataTargetSupported =
      selectedWriter.supportsReadOnlyLiveDataTarget === true;
    this.documentPrivacy = documentPrivacy ||
      new FinanceDocumentPrivacy({
        settings,
        app,
        now,
        evidenceDetector: () => selectedWriter.detectLicensedDataEvidence(),
      });
    this.registry = new FinanceOfficeOperationRegistry({
      documentPrivacy: this.documentPrivacy,
      writers: new Map([[command, selectedWriter]]),
      now,
    });
  }

  handles(toolName) {
    return Object.values(FINANCE_OFFICE_TOOL).includes(toolName);
  }

  supportsLiveDataTarget() {
    return this.liveDataTargetSupported;
  }

  async getDocumentPrivacyState() {
    return this.documentPrivacy.getWireMarker();
  }

  async getConnectorDocumentPrivacyState() {
    return this.documentPrivacy.getConnectorWireMarker();
  }

  async prepareLiveDataTarget(operationId, { signal } = {}) {
    if (!this.supportsLiveDataTarget()) {
      throw controllerError(
        'finance_office_target_unavailable',
        'This Office host does not have a stable read-only live data target'
      );
    }
    const writer = [...this.registry.writers.values()][0];
    if (typeof writer?.prepareLiveDataTarget !== 'function') {
      throw controllerError(
        'finance_office_target_unavailable',
        'A stable Office target could not be prepared'
      );
    }
    const documentPrivacy =
      await this.documentPrivacy.getConnectorWireMarker();
    const officeTarget =
      await writer.prepareLiveDataTarget({
        operationId,
        signal,
        assertCurrentIdentity: () =>
          this.documentPrivacy.assertCurrentFileIdentity(
            documentPrivacy.documentId
          ),
      });
    const revalidatedPrivacy =
      await this.documentPrivacy.getConnectorWireMarker();
    if (
      !officeTarget ||
      officeTarget.app !== this.app ||
      officeTarget.requireEmpty !== true ||
      revalidatedPrivacy.documentId !== documentPrivacy.documentId
    ) {
      throw controllerError(
        'finance_office_target_unavailable',
        'A stable Office target could not be prepared'
      );
    }
    return Object.freeze({
      documentPrivacy,
      officeTarget: Object.freeze({ ...officeTarget }),
    });
  }

  async execute(toolName, args, {
    signal,
    privateRequest = false,
  } = {}) {
    validateFinanceOfficeToolName(toolName);
    if (privateRequest !== true) {
      throw controllerError(
        'finance_office_private_boundary_required',
        'Finance Office operations require the private connector boundary'
      );
    }
    if (!args || args.app !== this.app) {
      throw controllerError(
        'finance_office_host_mismatch',
        'Finance Office operation targets a different Office host'
      );
    }

    let receipt;
    if (toolName === FINANCE_OFFICE_TOOL.PREPARE) {
      receipt = await this.registry.prepare(args, { signal });
    } else if (toolName === FINANCE_OFFICE_TOOL.COMMIT) {
      receipt = await this.registry.commit(args, { signal });
    } else if (toolName === FINANCE_OFFICE_TOOL.RECOVER) {
      receipt = await this.registry.recover(args, { signal });
    } else {
      throw controllerError(
        'finance_office_command_unsupported',
        'Finance Office command is not supported'
      );
    }
    return safeReceipt(receipt);
  }
}

export default FinanceOfficeController;
